import { Component, computed, Directive, input } from '@angular/core';
import { cn } from './cn';

/**
 * Presentational dialog shell primitives. Composition:
 *
 *   <hlm-dialog-surface>
 *     <hlm-dialog-header>
 *       <hlm-dialog-icon-badge tone="primary"><hlm-icon .../></hlm-dialog-icon-badge>
 *       <div hlmDialogHeading>
 *         <h2 hlmDialogTitle>Title</h2>
 *         <p hlmDialogDescription>Subtitle</p>
 *       </div>
 *       <button hlmDialogClose (click)="close()"><hlm-icon name="lucideX"/></button>
 *     </hlm-dialog-header>
 *     <section hlmDialogBody>...</section>
 *     <footer hlmDialogFooter>
 *       <button hlmBtn variant="outline">Cancel</button>
 *       <button hlmBtn>Save</button>
 *     </footer>
 *   </hlm-dialog-surface>
 */

@Component({
  selector: 'hlm-dialog-surface',
  standalone: true,
  template: '<ng-content />',
  host: { '[class]': '_c()' },
})
export class HlmDialogSurfaceComponent {
  readonly userClass = input<string>('', { alias: 'class' });
  readonly size = input<'sm' | 'md' | 'lg' | 'xl'>('md');
  protected readonly _c = computed(() => {
    // Min-widths are capped to the viewport (minus a 1rem gutter each side) so
    // the surface can shrink on narrow screens instead of overflowing the
    // dialog panel and clipping the footer actions (EB-3856). Pairs with the
    // global MatDialog maxWidth in app.config.ts.
    const sizes: Record<string, string> = {
      sm: 'min-w-[min(380px,calc(100vw_-_2rem))] max-w-[440px]',
      md: 'min-w-[min(480px,calc(100vw_-_2rem))] max-w-[600px]',
      lg: 'min-w-[min(640px,calc(100vw_-_2rem))] max-w-[880px]',
      xl: 'min-w-[min(900px,calc(100vw_-_2rem))] max-w-[1200px]',
    };
    // overflow-clip (not overflow-hidden): clips identically for the rounded corners but
    // makes the surface a non-scroll container. overflow-hidden is still programmatically
    // scrollable, so focusing a control inside a nested scroll area (e.g. a checkbox in the
    // Cancel Customer reason list) let the browser scrollIntoView-scroll the whole surface,
    // shoving the header off-screen and leaving a large empty gap below the footer (EB-3881).
    // The body ([hlmDialogBody]) remains the intended scroll container.
    return cn(
      'relative flex max-h-[88vh] flex-col overflow-clip rounded-2xl border border-border bg-card text-foreground shadow-[0_4px_12px_rgba(20,18,30,0.08),0_32px_64px_-16px_rgba(20,18,30,0.25)]',
      sizes[this.size()],
      this.userClass(),
    );
  });
}

@Component({
  selector: 'hlm-dialog-header',
  standalone: true,
  template: '<ng-content />',
  host: { '[class]': '_c()' },
})
export class HlmDialogHeaderComponent {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn(
      'relative flex items-start gap-3 border-b border-border px-5 pb-4 pt-5',
      this.userClass(),
    ),
  );
}

@Component({
  selector: 'hlm-dialog-icon-badge',
  standalone: true,
  template: '<ng-content />',
  host: { '[class]': '_c()' },
})
export class HlmDialogIconBadgeComponent {
  readonly tone = input<'primary' | 'destructive' | 'warning' | 'success' | 'muted'>('primary');
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() => {
    const tones: Record<string, string> = {
      primary: 'bg-primary/10 text-primary',
      destructive: 'bg-destructive/10 text-destructive',
      warning: 'bg-accent/15 text-accent',
      success: 'bg-success/10 text-success',
      muted: 'bg-muted text-muted-foreground',
    };
    return cn(
      'grid h-10 w-10 flex-none place-items-center rounded-xl',
      tones[this.tone()],
      this.userClass(),
    );
  });
}

@Directive({
  selector: '[hlmDialogHeading]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmDialogHeadingDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() => cn('min-w-0 flex-1 pt-0.5', this.userClass()));
}

@Directive({
  selector: '[hlmDialogTitle]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmDialogTitleDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn('m-0 text-base font-semibold tracking-tight text-foreground', this.userClass()),
  );
}

@Directive({
  selector: '[hlmDialogDescription]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmDialogDescriptionDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn('mt-0.5 text-sm text-muted-foreground', this.userClass()),
  );
}

@Directive({
  selector: '[hlmDialogClose]',
  standalone: true,
  host: {
    '[class]': '_c()',
    '[attr.aria-label]': '"Close dialog"',
    '[attr.tabindex]': '"-1"',
    type: 'button',
  },
})
export class HlmDialogCloseDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn(
      'absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:pointer-events-none disabled:opacity-50',
      this.userClass(),
    ),
  );
}

@Directive({
  selector: '[hlmDialogBody]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmDialogBodyDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-5', this.userClass()),
  );
}

@Directive({
  selector: '[hlmDialogFooter]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmDialogFooterDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn(
      'flex items-center justify-end gap-2 border-t border-border bg-muted/40 px-5 py-3',
      this.userClass(),
    ),
  );
}

/** Re-export bundle */
export const HLM_DIALOG_SHELL = [
  HlmDialogSurfaceComponent,
  HlmDialogHeaderComponent,
  HlmDialogIconBadgeComponent,
  HlmDialogHeadingDirective,
  HlmDialogTitleDirective,
  HlmDialogDescriptionDirective,
  HlmDialogCloseDirective,
  HlmDialogBodyDirective,
  HlmDialogFooterDirective,
] as const;
